import * as React from 'react';
import * as API from '../api';
import { displayUserNameLong } from '../helpers/displayHelpers';
import Modal from 'antd/lib/modal';
import { useForm } from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import Text from 'antd/lib/typography/Text';
import Select from 'antd/lib/select';
import DatePicker from 'antd/lib/date-picker';
import { useDispatch, useSelector } from 'react-redux';
import {
    NewReleasePayloadType,
    NewReleaseRequest,
    tryNewReleaseAction,
    tryUpdateReleaseEntity,
    TRY_NEW_RELEASE,
    TRY_UPDATE_RELEASE_ENTITY,
    UpdateReleasePayloadType,
    UpdateReleaseRequest,
    CreateEditReleaseType,
} from '../actions/release';
import { useHistory, useParams } from 'react-router-dom';
import { useAppSettings } from '@/utils/hooks/helpers';
import { AppComponentProps, AppRouteComponentMatchParams, Nullable } from '../typescriptHelpers';
import { RootState } from '../reducers/reducers';
import {
    getError,
    getIsAppleAppStore,
    getIsGooglePlayStore,
    getIsLoading,
    getUpdated,
    isAppPlatform,
    releaseTypeToCreateEditReleaseType,
} from '../helpers';
import Divider from 'antd/lib/divider';
import { Today, formatDateWithTimezone } from '../timeHelpers';
import { dayjs } from '@/extendedDayjs';
import { createAppRoute } from '../helpers/routeHelpers';
import Input from 'antd/lib/input/Input';
import { flatMap } from 'lodash';
import {
    selectAuthCredentials,
    selectAppPlatform,
    selectReleasePilotEligibleUsersForApp,
    selectKickedOffAt,
    selectRelease,
    selectAppStoreType,
    selectIsDemoInstance,
    selectIntegrationForCapability,
    selectOrgUser,
    selectAppSettings,
    selectAppTimeFormatSetting,
    selectCadenceTimezone,
} from '../selectors/selectors';
import { PredictReleaseVersionRequest, ValidateReleaseVersionRequest } from '../actions/app';
import { globalMessage } from '../utils/globalMessage';
import {
    NewVersionValidationResult,
    SubscriptionFeatureType,
    SettingType,
    IntegrationCapability,
    ReleaseType,
    ReleaseScheduleCadenceStyle,
    PatternToken,
} from '../types';
import { PlanRestrictedFeatureWrapper } from './planRestrictions/PlanRestrictedFeatureWrapper';
import Alert from 'antd/lib/alert';
import Form from 'antd/lib/form';
import { Link } from 'react-router-dom';
import { createAppSettingsRouteWithSettingType } from '../helpers/routeHelpers';
import { FROM_ROTATION } from '../constants';
import IntegrationIcon from './icons/IntegrationIcon';
import Space from 'antd/lib/space';
import { runwayColors, titleStyle, antGrey } from './styles/globalStyles';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';
import { Checkbox, Flex } from 'antd/lib';

interface CreateEditReleaseModalProps extends AppComponentProps {
    open: boolean;
    releaseId?: string;
    isEditingRelease: boolean;
    displayOnlyTargetDates: boolean;
    onDidCreateOrEditRelease: () => void;
    onCancel: () => void;
    canBeSubmitted: Nullable<boolean>;
    onEditTargetDates?: () => void;
}

const CreateEditReleaseModal = (props: CreateEditReleaseModalProps) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const history = useHistory();

    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));

    const release = useSelector((state: RootState) => {
        if (props.releaseId && props.isEditingRelease) {
            return selectRelease(state, props.releaseId);
        }
        return null;
    });

    const kickedOffAt = useSelector((state: RootState) => {
        if (props.isEditingRelease && props.releaseId) {
            return selectKickedOffAt(state, props.releaseId);
        } else {
            return null;
        }
    });

    const [formValid, setFormValid] = React.useState(true);
    const [predictedPilotId, setPredictedPilotId] = React.useState(null);
    const [isReleasePilotFromRotationScheduleIntegration, setIsReleasePilotFromRotationScheduleIntegration] =
        React.useState(false);
    const [showOverridePilotAlert, setShowOverridePilotAlert] = React.useState(false);

    const [form] = useForm();
    const dispatch = useDispatch();

    const appSettings = useAppSettings(props);
    const scheduleSettings = appSettings.releaseScheduleCadenceSettings;

    const scheduleIsActive =
        !!scheduleSettings && scheduleSettings.scheduleCadence !== ReleaseScheduleCadenceStyle.Never;

    const scheduleIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, props.appId, IntegrationCapability.Scheduling),
    );

    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const pilotUsers = useSelector((state: RootState) => selectReleasePilotEligibleUsersForApp(state, props.appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const cadenceTimezone = useSelector((state: RootState) => selectCadenceTimezone(state, props.appId));

    const draftNewReleaseIsLoading = useSelector((state: RootState) => getIsLoading(TRY_NEW_RELEASE, state));
    const draftNewReleaseSuccess = useSelector((state: RootState) => getUpdated(TRY_NEW_RELEASE, state));
    const draftNewReleaseFailure = useSelector((state: RootState) => getError(TRY_NEW_RELEASE, state));

    const editReleaseIsLoading = useSelector((state: RootState) => getIsLoading(TRY_UPDATE_RELEASE_ENTITY, state));
    const editReleaseFailure = useSelector((state: RootState) => getError(TRY_UPDATE_RELEASE_ENTITY, state));
    const editReleaseSuccess = useSelector((state: RootState) => getUpdated(TRY_UPDATE_RELEASE_ENTITY, state));

    const predictedPilotUser = useSelector((state: RootState) => selectOrgUser(state, props.orgId, predictedPilotId));
    const [resultIsStaticBranch, setResultIsStaticBranch] = React.useState(false);

    const createOrUpdateRelease = (
        isEditing: boolean,
        version?: string,
        releaseType?: CreateEditReleaseType,
        releasePilotId?: string,
        targetKickoffDate?: dayjs.Dayjs,
        targetSubmitDate?: dayjs.Dayjs,
        targetReleaseDate?: dayjs.Dayjs,
        releaseName?: string,
        shouldMatchPreviousPhasedRelease?: boolean,
    ) => {
        if (isEditing) {
            const request: UpdateReleaseRequest = {
                orgId: props.orgId,
                appId: props.appId,
                releaseId: props.releaseId,
                releaseType: releaseType,
                version: version,
                releasePilotId: releasePilotId,
                targetKickoffDate: targetKickoffDate ? targetKickoffDate.startOf('minute').toISOString() : null,
                targetSubmitDate: targetSubmitDate ? targetSubmitDate.startOf('minute').toISOString() : null,
                targetReleaseDate: targetReleaseDate ? targetReleaseDate.startOf('minute').toISOString() : null,
                releaseName: releaseName,
                shouldMatchPreviousPhasedRelease,
            };

            updateRelease(request);
        } else {
            const request: NewReleaseRequest = {
                orgId: props.orgId,
                appId: props.appId,
                releaseType: releaseType,
                releasePilotId: releasePilotId,
                version: version,
                targetKickoffDate: targetKickoffDate ? targetKickoffDate.startOf('hour').toISOString() : null,
                targetSubmitDate: targetSubmitDate ? targetSubmitDate.startOf('hour').toISOString() : null,
                targetReleaseDate: targetReleaseDate ? targetReleaseDate.startOf('hour').toISOString() : null,
                releaseName: releaseName,
            };

            createNewRelease(request);
        }
    };

    const submitForm = () => {
        const values = form.getFieldsValue();
        createOrUpdateRelease(
            props.isEditingRelease,
            values.releaseVersion,
            releaseTypeToCreateEditReleaseType(release?.type ?? ReleaseType.Minor),
            values.releasePilotId,
            values.targetKickoffDate,
            values.targetSubmitDate,
            values.targetReleaseDate,
            values.releaseName,
            values.shouldMatchPreviousPhasedRelease,
        );
    };

    const validateForm = () => {
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));
        const isValid = errors.length === 0;
        setFormValid(isValid);
    };

    const createNewRelease = (newReleaseRequest: NewReleaseRequest) => {
        const request: NewReleasePayloadType = {
            orgId: props.orgId,
            orgIdParam: orgIdParam,
            appId: props.appId,
            appIdParam: appIdParam,
            request: {
                ...newReleaseRequest,
            },
            isRollbackConversion: false,
        };

        dispatch(tryNewReleaseAction(request));
    };

    const updateRelease = (releaseRequest: UpdateReleaseRequest) => {
        const request: UpdateReleasePayloadType = {
            orgId: props.orgId,
            orgIdParam: orgIdParam,
            appId: props.appId,
            appIdParam: appIdParam,
            request: {
                ...releaseRequest,
            },
        };

        dispatch(tryUpdateReleaseEntity(request));
    };

    React.useEffect(() => {
        if (editReleaseSuccess && props.onEditTargetDates) {
            props.onEditTargetDates();
        }
    }, [dispatch, editReleaseSuccess]);

    const versionRegex = /\d+(?:\.\d+)?(?:\.\d+)?/;

    const validateRelease = (version, sourceVersion) => {
        const request: ValidateReleaseVersionRequest = {
            orgId: props.orgId,
            appId: props.appId,
            version: version,
            releaseId: props.releaseId ?? null,
            releaseType: CreateEditReleaseType.Standard,
            sourceVersion: sourceVersion,
        };

        return API.validateReleaseVersion(request, authCredentials)
            .then((result: NewVersionValidationResult) => {
                setResultIsStaticBranch(result.isStaticBranch);
                setPredictedPilotId(result.releasePilotFromRotation);
                setIsReleasePilotFromRotationScheduleIntegration(result.isReleasePilotFromRotationScheduleIntegration);

                return Promise.resolve(result);
            })
            .catch((reason) => {
                return Promise.reject(reason);
            });
    };

    const validateVersion = (rule, version) => {
        if (!version || !versionRegex.test(version)) {
            return Promise.reject('Version must contain only numbers and up to 3 segments');
        }

        const values = form.getFieldsValue();
        return validateRelease(version, values.sourceVersion);
    };

    const isDemoInstance = useSelector((state: RootState) => selectIsDemoInstance(state, props.orgId));

    const setPredictedVersion = (isHotfix: boolean) => {
        const request: PredictReleaseVersionRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId ?? null,
            isHotfix,
        };

        return API.predictReleaseVersion(request, authCredentials)
            .then((version) => {
                form.setFieldValue('releaseVersion', version);
            })
            .catch(() => {
                form.setFieldValue('releaseVersion', isDemoInstance ? '1.3.0' : '1.5.0');
                return Promise.resolve();
            });
    };

    React.useEffect(() => {
        if (props.isEditingRelease && props.open && release) {
            form.setFieldsValue({
                releaseVersion: release.version,
                targetKickoffDate: release.targetKickoffDate ? dayjs(release.targetKickoffDate) : null,
                targetSubmitDate: release.targetSubmitDate ? dayjs(release.targetSubmitDate) : null,
                targetReleaseDate: release.targetReleaseDate ? dayjs(release.targetReleaseDate) : null,
                releaseName: release.releaseName,
                sourceVersion: release.sourceVersion,
                shouldMatchPreviousPhasedRelease: release.shouldMatchPreviousPhasedRelease,
            });

            let releasePilotId = release.releasePilotId;

            validateRelease(release.version, release.sourceVersion)
                .then((result: NewVersionValidationResult) => {
                    if (result.releasePilotFromRotation && result.releasePilotFromRotation === releasePilotId) {
                        releasePilotId = FROM_ROTATION;
                    }
                })
                .finally(() => {
                    form.setFieldValue('releasePilotId', releasePilotId);
                });
        }
    }, [props.open]);

    React.useEffect(() => {
        if (props.open && !props.isEditingRelease) {
            form.setFieldsValue({
                releasePilotId: null,
                releaseVersion: null,
                targetKickoffDate: null,
                targetSubmitDate: null,
                targetReleaseDate: null,
                releaseName: null,
                sourceVersion: null,
                shouldMatchPreviousPhasedRelease: null,
            });

            setPredictedVersion(false).then(() => {
                form.validateFields();
            });
        }
    }, [props.open]);

    React.useEffect(() => {
        if (draftNewReleaseFailure && !props.isEditingRelease && props.open) {
            globalMessage.error(draftNewReleaseFailure);
        }
    }, [draftNewReleaseFailure]);

    React.useEffect(() => {
        if (draftNewReleaseSuccess && !props.isEditingRelease && props.open) {
            props.onDidCreateOrEditRelease();

            globalMessage.success('New release created.');
        }
    }, [draftNewReleaseSuccess]);

    React.useEffect(() => {
        if (editReleaseFailure && props.isEditingRelease && props.open) {
            globalMessage.error(editReleaseFailure);
        }
    }, [editReleaseFailure]);

    React.useEffect(() => {
        if (editReleaseSuccess && props.isEditingRelease && props.open) {
            const newVersion = form.getFieldValue('releaseVersion');
            const previousVersion = release?.version;
            const versionChanged = newVersion && newVersion !== previousVersion;

            if (versionChanged) {
                globalMessage.success('Release version and settings updated!');

                const appRoute = createAppRoute(orgIdParam, appIdParam);

                history.replace(appRoute);
            } else {
                globalMessage.success('Release settings updated!');

                props.onDidCreateOrEditRelease();
            }
        }
    }, [editReleaseSuccess, props.isEditingRelease]);

    const title =
        props.displayOnlyTargetDates ? 'Edit target dates'
        : props.isEditingRelease ? 'Edit release settings'
        : 'Prepare a new release';
    const buttonTitle = props.isEditingRelease && release ? `Save` : 'Prepare release';
    const disableReleaseVersion =
        props.isEditingRelease && release ? release.doesBranchExist && !release.isStaticReleaseBranch : false;

    const actualKickoffDate = kickedOffAt ? dayjs(kickedOffAt) : null;
    const submittedAt = release?.submittedAt;
    const actualSubmitDate = submittedAt ? dayjs(submittedAt) : null;
    const releasedOn = release?.releasedAt;
    const actualReleasedDate = releasedOn ? dayjs(releasedOn) : null;

    const isAppleAppStore = getIsAppleAppStore(appStoreType);
    const predictedPilotDisplayName = predictedPilotUser ? displayUserNameLong(predictedPilotUser) : null;
    const currentPilotIsRotationPilot = release?.releasePilotId ? release.releasePilotId === predictedPilotId : false;
    const scheduleIntegrationIcon =
        scheduleIntegration ? <IntegrationIcon integrationId={scheduleIntegration.id} /> : null;

    const handleSelectPilotChange = (value) => {
        if (value !== FROM_ROTATION && currentPilotIsRotationPilot) {
            setShowOverridePilotAlert(true);
        } else {
            setShowOverridePilotAlert(false);
        }
    };

    const releaseBranchPatterns = useSelector((state: RootState) => {
        const appSettings = selectAppSettings(state, props.appId);

        return appSettings.releaseBranchPatterns.map((releaseBranchPattern) => releaseBranchPattern.pattern);
    });

    const foundAnyBranchPatternsUsingReleaseNameToken = releaseBranchPatterns.reduce((foundAnyMatch, pattern) => {
        return foundAnyMatch ? foundAnyMatch : pattern.includes(PatternToken.ReleaseName);
    }, false);

    const disableEditReleaseName = foundAnyBranchPatternsUsingReleaseNameToken && disableReleaseVersion;

    const isGooglePlayStore = getIsGooglePlayStore(appStoreType);
    const isHotfix = release?.type === ReleaseType.Hotfix;
    const shouldShowShouldMatchPreviousPhasedReleaseOption = props.isEditingRelease && isHotfix && isGooglePlayStore;

    return (
        <Modal
            open={props.open}
            title={title}
            okText={buttonTitle}
            okButtonProps={{ disabled: !formValid }}
            onOk={() => submitForm()}
            confirmLoading={draftNewReleaseIsLoading || editReleaseIsLoading}
            onCancel={props.onCancel}
            afterClose={() => setShowOverridePilotAlert(false)}
            width={650}
        >
            <Form form={form} onFieldsChange={validateForm}>
                <FormItem
                    label="Release version"
                    name="releaseVersion"
                    rules={[
                        {
                            validator: validateVersion,
                        },
                        {
                            required: true,
                            message: 'Version is required',
                        },
                    ]}
                    hidden={props.displayOnlyTargetDates}
                    help={
                        disableReleaseVersion ?
                            'You cannot change the version or hotfix selection if a release branch has already been created for this version.'
                        :   null
                    }
                    labelCol={{ flex: '130px' }}
                >
                    <Input style={{ fontSize: 18 }} disabled={disableReleaseVersion} autoComplete="off" />
                </FormItem>
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.ReleasePilots}>
                    {(isDisabled) => (
                        <>
                            <FormItem
                                label={
                                    <PlanRestrictedFeatureWrapper
                                        feature={SubscriptionFeatureType.ReleasePilots}
                                        requiresTooltip
                                    >
                                        <Text>Release pilot</Text>
                                    </PlanRestrictedFeatureWrapper>
                                }
                                name="releasePilotId"
                                hidden={props.displayOnlyTargetDates}
                                labelCol={{ flex: '130px' }}
                                help={
                                    isReleasePilotFromRotationScheduleIntegration ?
                                        `Release pilots are being managed from ${
                                            scheduleIntegration ?
                                                scheduleIntegration?.name
                                            :   'your Scheduling integration'
                                        }`
                                    :   null
                                }
                            >
                                <Select
                                    placeholder="Select"
                                    disabled={isDisabled || isReleasePilotFromRotationScheduleIntegration}
                                    onChange={handleSelectPilotChange}
                                >
                                    <Select.Option value={FROM_ROTATION}>
                                        <Space align="center">
                                            {isReleasePilotFromRotationScheduleIntegration && scheduleIntegrationIcon}
                                            <Text>
                                                From rotation{' '}
                                                {predictedPilotDisplayName != null && (
                                                    <Text type="secondary">({predictedPilotDisplayName})</Text>
                                                )}
                                            </Text>
                                        </Space>
                                    </Select.Option>
                                    {pilotUsers.map((user) => {
                                        return (
                                            <Select.Option key={user.id} value={user.id}>
                                                <Space align="center">
                                                    {isReleasePilotFromRotationScheduleIntegration &&
                                                        scheduleIntegrationIcon}
                                                    <Text> {displayUserNameLong(user)}</Text>
                                                </Space>
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                            </FormItem>
                            {showOverridePilotAlert && (
                                <Alert
                                    style={{ marginTop: 10, marginBottom: 16 }}
                                    type="warning"
                                    description={
                                        <>
                                            This will replace the existing pilot{' '}
                                            {predictedPilotDisplayName ?
                                                <>
                                                    (<b>{predictedPilotDisplayName}</b>){' '}
                                                </>
                                            :   ''}
                                            from your rotation for this release. The rotation will continue for your
                                            next release. Visit{' '}
                                            <Link
                                                to={createAppSettingsRouteWithSettingType(
                                                    orgIdParam,
                                                    appIdParam,
                                                    SettingType.ReleasePilotRotation,
                                                )}
                                            >
                                                App settings {'>'} Release pilot rotation
                                            </Link>{' '}
                                            to manage your rotation.
                                        </>
                                    }
                                />
                            )}
                        </>
                    )}
                </PlanRestrictedFeatureWrapper>
                <FormItem
                    label="Release name"
                    name="releaseName"
                    hidden={props.displayOnlyTargetDates}
                    rules={[{ required: false }]}
                    extra={<span style={{ fontSize: 13 }}>A human-friendly name for your release</span>}
                    labelCol={{ flex: '130px' }}
                >
                    <Input style={{ fontSize: 18 }} disabled={disableEditReleaseName} autoComplete="off" />
                </FormItem>
                {!props.displayOnlyTargetDates && <Divider />}
                {scheduleIsActive && (
                    <>
                        <FormItem noStyle>
                            <Text
                                style={{
                                    ...titleStyle,
                                    fontWeight: 400,
                                    fontSize: 13,
                                    marginBottom: 0,
                                    display: 'block',
                                    color: runwayColors.black,
                                }}
                            >
                                {isAppPlatform(appPlatform) && isAppleAppStore ?
                                    'Set target kickoff/submit/release dates'
                                : isAppPlatform(appPlatform) && !isAppleAppStore ?
                                    'Set target kickoff and submit dates'
                                :   'Set target kickoff date'}
                            </Text>
                        </FormItem>
                        <FormItem
                            name="noTargetDates"
                            label={
                                <span style={{ color: antGrey.grey7, fontSize: 13 }}>
                                    {
                                        'If no target dates are set here, Runway will attempt to apply the schedule configured in App settings > Schedule.'
                                    }
                                </span>
                            }
                            labelCol={{ span: 24 }}
                        ></FormItem>
                    </>
                )}
                <FormItem
                    label="Target kickoff date"
                    name="targetKickoffDate"
                    extra={
                        <span style={{ fontSize: 13 }}>
                            {actualKickoffDate ?
                                `Your release was kicked off on ${formatDateWithTimezone(
                                    actualKickoffDate,
                                    'timezone',
                                    cadenceTimezone,
                                    use24HourTimeFormat,
                                )}`
                            : release?.isStaticReleaseBranch === true || resultIsStaticBranch ?
                                'Your release is kicked off when new code is detected past the last tag or promoted to the release branch by Runway.'
                            :   'Your release is kicked off when a release branch is detected or created by Runway.'}
                        </span>
                    }
                    rules={[
                        {
                            validator: (rule, value) => {
                                const existingKickoffValue =
                                    release?.targetKickoffDate ? dayjs(release.targetKickoffDate) : null;

                                const hourIsSameAsExisting =
                                    existingKickoffValue ? existingKickoffValue.isSame(value, 'hour') : false;

                                const minuteIsSameAsExisting =
                                    existingKickoffValue ? existingKickoffValue.isSame(value, 'minute') : false;

                                if (hourIsSameAsExisting && minuteIsSameAsExisting) {
                                    // don't validate if value is the same as previously set
                                    return Promise.resolve();
                                }

                                if (value && value.isBefore(Today, 'day')) {
                                    return Promise.reject(`Target kickoff date cannot be before today`);
                                } else {
                                    return Promise.resolve();
                                }
                            },
                        },
                    ]}
                >
                    <DatePicker
                        needConfirm={false}
                        showTime={{ defaultValue: dayjs('12:00:00', 'HH:mm:ss') }}
                        disabledDate={(date) => {
                            return date.isBefore(Today, 'day');
                        }}
                        showNow={false}
                        format={(date) =>
                            formatDateWithTimezone(date, 'shortWithTimezone', cadenceTimezone, use24HourTimeFormat)
                        }
                        minuteStep={15}
                    />
                </FormItem>
                {isAppPlatform(appPlatform) && (
                    <FormItem
                        label={'Target submit date'}
                        name="targetSubmitDate"
                        extra={
                            actualSubmitDate ?
                                <span style={{ fontSize: 13 }}>
                                    Your release was submitted on{' '}
                                    {formatDateWithTimezone(
                                        actualSubmitDate,
                                        'timezone',
                                        cadenceTimezone,
                                        use24HourTimeFormat,
                                    )}
                                </span>
                            :   null
                        }
                        rules={[
                            {
                                validator: (rule, value) => {
                                    const existingSubmitValue =
                                        release?.targetSubmitDate ? dayjs(release.targetSubmitDate) : null;
                                    const existingTargetKickoffDate =
                                        release?.targetKickoffDate ? dayjs(release.targetKickoffDate) : null;
                                    const valueIsSameAsExisting =
                                        existingSubmitValue ? existingSubmitValue.isSame(value, 'day') : false;

                                    const wasKickedOffBeforeTargetDate =
                                        actualKickoffDate && existingTargetKickoffDate ?
                                            actualKickoffDate.isBefore(existingTargetKickoffDate)
                                        :   false;

                                    if (valueIsSameAsExisting) {
                                        // don't validate if value is the same as previously set
                                        return Promise.resolve();
                                    }

                                    const targetKickoff = form.getFieldValue('targetKickoffDate');

                                    const valueIsAfterToday = (date) => {
                                        if (date && date.isBefore(Today, 'day')) {
                                            return Promise.reject(`Target submit date cannot be before today`);
                                        } else {
                                            return Promise.resolve();
                                        }
                                    };

                                    if (targetKickoff) {
                                        if (
                                            value &&
                                            !wasKickedOffBeforeTargetDate &&
                                            value.isBefore(targetKickoff, 'minute')
                                        ) {
                                            return Promise.reject(
                                                `Target submit date cannot be before target kickoff date`,
                                            );
                                        } else if (
                                            value &&
                                            wasKickedOffBeforeTargetDate &&
                                            value.isBefore(actualKickoffDate, 'minute')
                                        ) {
                                            return Promise.reject(
                                                `Target submit date cannot be before kicked off date`,
                                            );
                                        }
                                        return valueIsAfterToday(value);
                                    } else {
                                        return valueIsAfterToday(value);
                                    }
                                },
                            },
                        ]}
                    >
                        <DatePicker
                            needConfirm={false}
                            showTime={{
                                defaultValue: dayjs('12:00', 'HH:mm'),
                            }}
                            disabledDate={(date) => {
                                return date.isBefore(Today, 'day');
                            }}
                            showNow={false}
                            minuteStep={15}
                            format={(date) =>
                                formatDateWithTimezone(date, 'shortWithTimezone', cadenceTimezone, use24HourTimeFormat)
                            }
                        />
                    </FormItem>
                )}
                {isAppleAppStore && (
                    <FormItem
                        label={'Target release date'}
                        name="targetReleaseDate"
                        extra={
                            <span style={{ fontSize: 13 }}>
                                {actualReleasedDate ?
                                    `Your release was released on ${formatDateWithTimezone(
                                        actualReleasedDate,
                                        'timezone',
                                        cadenceTimezone,
                                        use24HourTimeFormat,
                                    )}`
                                :   null}
                            </span>
                        }
                        rules={[
                            {
                                validator: (rule, value) => {
                                    const existingReleaseValue =
                                        release?.targetReleaseDate ? dayjs(release.targetReleaseDate) : null;
                                    const valueIsSameAsExisting =
                                        existingReleaseValue ? existingReleaseValue.isSame(value, 'day') : false;
                                    const existingTargetSubmitDate =
                                        release?.targetSubmitDate ? dayjs(release.targetSubmitDate) : null;

                                    const wasSubmittedBeforeTargetDate =
                                        actualSubmitDate && existingTargetSubmitDate ?
                                            actualSubmitDate.isBefore(existingTargetSubmitDate)
                                        :   false;

                                    if (valueIsSameAsExisting) {
                                        // don't validate if value is the same as previously set
                                        return Promise.resolve();
                                    }

                                    const targetSubmit = form.getFieldValue('targetSubmitDate');

                                    const valueIsAfterToday = (date) => {
                                        if (date && date.isBefore(Today, 'day')) {
                                            return Promise.reject(`Target release date cannot be before today`);
                                        } else {
                                            return Promise.resolve();
                                        }
                                    };

                                    if (targetSubmit) {
                                        if (
                                            value &&
                                            !wasSubmittedBeforeTargetDate &&
                                            value.isBefore(targetSubmit, 'minute')
                                        ) {
                                            return Promise.reject(
                                                `Target release date cannot be before your target submit date`,
                                            );
                                        } else if (
                                            value &&
                                            wasSubmittedBeforeTargetDate &&
                                            value.isBefore(actualSubmitDate, 'minute')
                                        ) {
                                            return Promise.reject(
                                                `Target release date cannot be before submitted date`,
                                            );
                                        }
                                        return valueIsAfterToday(value);
                                    } else {
                                        return valueIsAfterToday(value);
                                    }
                                },
                            },
                        ]}
                    >
                        <DatePicker
                            needConfirm={false}
                            showTime={{
                                defaultValue: dayjs('12:00', 'HH:mm'),
                            }}
                            disabledDate={(date) => {
                                return date.isBefore(Today, 'day');
                            }}
                            minuteStep={15}
                            showNow={false}
                            format={(date) =>
                                formatDateWithTimezone(date, 'shortWithTimezone', cadenceTimezone, use24HourTimeFormat)
                            }
                        />
                    </FormItem>
                )}
                <Divider />
                {shouldShowShouldMatchPreviousPhasedReleaseOption && (
                    <FormItem name="shouldMatchPreviousPhasedRelease" valuePropName="checked">
                        <Flex align="center">
                            <Checkbox defaultChecked={release?.shouldMatchPreviousPhasedRelease ?? false}>
                                Upon release, start rollout where previous release left off.
                            </Checkbox>
                            <TooltipInfoIcon tooltipContent="Runway will start the hotfix staged rollout at the same percentage where the previous release's rollout left off (determined at time of hotfix release), then will increment from there according to the remainder of your configured staged rollout sequence." />
                        </Flex>
                    </FormItem>
                )}
            </Form>
        </Modal>
    );
};

export default CreateEditReleaseModal;
